/**
 * F1 — UI surface for `RetryExhaustedError` and `GraphProbeFailedError`.
 *
 * Imperative API: `pushGraphErrorToast(err)` can be called from any code
 * path (React component, hook, plain function). A listener mounted near
 * the desktop root (`<GraphErrorToastHost />` in main.tsx) renders the
 * toast stack.
 *
 * Win95-styled, dismissible, auto-fades after 8s. No external toast
 * library — keep the dep graph small.
 *
 * Memory: feedback_audit_writer_pattern.md (same "leaf module emits,
 * SPA wires the UI" ethos).
 */

import { useEffect, useState } from 'react';
import { RetryExhaustedError, GraphProbeFailedError } from '@repancy/graph-core';

interface Toast {
  id: string;
  message: string;
  kind: 'write-failed' | 'read-failed' | 'probe-failed' | 'info' | 'other';
  ts: number;
  tone: 'error' | 'info';
  // K.11.4 — optional Microsoft docs URL for actionable errors. Toast
  // renders a "Learn more ↗" anchor when present. Useful when the
  // failure mode has a known remediation page (e.g. 403 → Sites.Selected
  // docs, 423 → sensitivity-labels docs).
  learnMoreUrl?: string | null;
  // Optional override for the bold header. Defaults to "SharePoint error"
  // for error-tone toasts, "SharePoint" for info-tone toasts.
  title?: string;
}

type Listener = (toasts: Toast[]) => void;
const listeners = new Set<Listener>();
let toasts: Toast[] = [];

function emit() {
  for (const l of listeners) l(toasts);
}

function add(t: Toast) {
  toasts = [...toasts, t];
  emit();
  // Auto-dismiss
  setTimeout(() => dismiss(t.id), 8000);
}

function dismiss(id: string) {
  toasts = toasts.filter((t) => t.id !== id);
  emit();
}

/** Imperative API. Safe to call from anywhere, including non-React code. */
export function pushGraphErrorToast(err: unknown, opts?: { learnMoreUrl?: string | null }): void {
  const t = classifyError(err);
  if (opts?.learnMoreUrl !== undefined) {
    t.learnMoreUrl = opts.learnMoreUrl;
  }
  add(t);
}

/**
 * Non-error success/status toast. Same registry + auto-dismiss as the
 * error variant, rendered in an info tone (no red border, neutral
 * header). Use for "Provisioning queued — job <id>" and similar
 * notifications that should NOT scream "error".
 */
export function pushGraphInfoToast(
  message: string,
  opts?: { title?: string; learnMoreUrl?: string | null },
): void {
  const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  add({
    id,
    ts: Date.now(),
    kind: 'info',
    tone: 'info',
    message,
    title: opts?.title,
    learnMoreUrl: opts?.learnMoreUrl ?? null,
  });
}

/**
 * K.11.4 — push a domain-specific error toast (not a Graph error
 * thrown by retry exhaustion). Used for connected-site permission
 * failures where the server-side action returns a structured
 * `{ ok:false, reason, message, learnMoreUrl }` rather than throwing.
 */
export function pushConnectedSitePermissionErrorToast(args: {
  message: string;
  learnMoreUrl: string | null;
}): void {
  const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  add({
    id,
    ts: Date.now(),
    kind: 'write-failed',
    tone: 'error',
    message: args.message,
    learnMoreUrl: args.learnMoreUrl,
  });
  // K.11.4 — telemetry. PostHog is currently inert (VITE_POSTHOG_KEY
  // unset in dev/prod per the K.6 handoff) but the gate lets it light
  // up automatically when wired without revisiting this code.
  try {
    const posthog = (window as unknown as { posthog?: { capture?: (event: string, props: object) => void } }).posthog;
    posthog?.capture?.('connected_site_permission_error', {
      message: args.message,
      learnMoreUrl: args.learnMoreUrl,
    });
  } catch {
    // never let telemetry block UX
  }
}

function classifyError(err: unknown): Toast {
  const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const ts = Date.now();
  if (err instanceof RetryExhaustedError) {
    if (err.policy.name === 'WRITE') {
      return {
        id,
        ts,
        kind: 'write-failed',
        tone: 'error',
        message: `Save to SharePoint failed after ${err.attempts} attempts. Your edit is still in the document — try again.`,
      };
    }
    return {
      id,
      ts,
      kind: 'read-failed',
      tone: 'error',
      message: `Couldn't reach SharePoint after ${err.attempts} attempts (${err.lastStatus ?? 'network'}). Try again or check connection.`,
    };
  }
  if (err instanceof GraphProbeFailedError) {
    return {
      id,
      ts,
      kind: 'probe-failed',
      tone: 'error',
      message: `Couldn't read permissions on that item. The badge shows ⚠️ Unknown until we can retry.`,
    };
  }
  return {
    id,
    ts,
    kind: 'other',
    tone: 'error',
    message: err instanceof Error ? err.message : String(err),
  };
}

/** React subscriber. */
export function useGraphErrorToasts(): { toasts: Toast[]; dismiss: (id: string) => void } {
  const [list, setList] = useState<Toast[]>(toasts);
  useEffect(() => {
    listeners.add(setList);
    setList(toasts);
    return () => {
      listeners.delete(setList);
    };
  }, []);
  return { toasts: list, dismiss };
}

/**
 * Mount once near the root. Renders the toast stack in the bottom-right
 * corner above the watermark (z-9996, watermark is z-9998 — toasts sit
 * UNDER the watermark on purpose so the watermark is never obscured;
 * adjust if customer feedback says toasts get hidden).
 *
 * Repancy uses a Win95-styled UI, so the toast borrows the same
 * bg/border palette.
 */
export function GraphErrorToastHost() {
  const { toasts: list, dismiss } = useGraphErrorToasts();
  if (list.length === 0) return null;
  return (
    <div
      data-testid="graph-error-toast-host"
      className="fixed bottom-12 left-3 z-[9996] flex max-w-[360px] flex-col gap-2"
    >
      {list.map((t) => {
        const isInfo = t.tone === 'info';
        const headerText = t.title ?? (isInfo ? 'SharePoint' : 'SharePoint error');
        const containerClass = isInfo
          ? 'rounded-sm border-2 border-[#316ac5] bg-[#f0f8ff] p-2 text-[12px] text-black shadow-md'
          : 'rounded-sm border-2 border-[#ff00aa] bg-[#fff5f9] p-2 text-[12px] text-black shadow-md';
        const headerClass = isInfo
          ? 'mb-1 font-bold text-[#006688]'
          : 'mb-1 font-bold text-[#ff00aa]';
        const dismissClass = isInfo
          ? 'cursor-pointer border border-[#316ac5] bg-white px-2 py-0.5 text-xs text-[#006688]'
          : 'cursor-pointer border border-[#ff00aa] bg-white px-2 py-0.5 text-xs text-[#ff00aa]';
        return (
          <div
            key={t.id}
            data-testid={`graph-error-toast-${t.kind}`}
            data-tone={t.tone}
            role={isInfo ? 'status' : 'alert'}
            className={containerClass}
          >
            <div className={headerClass}>{headerText}</div>
            <p className="m-0 mb-2 text-black">{t.message}</p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => dismiss(t.id)}
                data-testid={`graph-error-toast-dismiss-${t.kind}`}
                className={dismissClass}
              >
                Dismiss
              </button>
              {t.learnMoreUrl ? (
                <a
                  href={t.learnMoreUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`graph-error-toast-learn-more-${t.kind}`}
                  className="text-xs text-[#0000a0] underline hover:text-[#0000ff]"
                >
                  Learn more ↗
                </a>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Test-only: reset listeners + toast list between tests. */
export function __resetGraphErrorToastsForTests() {
  toasts = [];
  listeners.clear();
}
